import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, LayoutGrid, List, Search, Settings2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { cn } from '@Utils';

import { CredentialCardProps, PageWrapper } from '@Components';
import { Button } from '@Components/ui-v2/button';
import { ExpiringCredential } from '@Models';
import { RootState } from '@Store';
import config from '../../config';
import CredentialCard from './components/CredentialCard';
import CredentialListView from './components/CredentialListView';
import CredentialModal from './components/CredentialModal';
import EmptyState from './components/EmptyState';
import SectionLabel from './components/SectionLabel';
import buildCardProps from './buildCardProps';
import { CredentialEntry, SortMode, ViewMode } from './credentials.types';

const AVAILABLE_CONNECTOR_ALLOWLIST = new Set(['Walmart', 'Soriana']);

const STATUS_SORT_RANK: Record<string, number> = {
  expired: 0,
  expiring: 1,
  connected: 2,
  available: 3,
};

interface CredentialsProps {
  credentialUpdated: () => void;
  credentials: CredentialCardProps[];
  expiringCredentials: ExpiringCredential[];
}

const Credentials = ({ credentials, credentialUpdated, expiringCredentials }: CredentialsProps) => {
  const { t } = useTranslation();

  const [openInfoId, setOpenInfoId] = useState<string | null>(null);
  const [modalId, setModalId] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem('bops.cred.view');
      return stored === 'cards' || stored === 'list' ? stored : 'cards';
    } catch {
      return 'cards';
    }
  });
  const [sort, setSort] = useState<SortMode>(() => {
    try {
      const stored = localStorage.getItem('bops.cred.sort');
      return stored === 'default' || stored === 'status' || stored === 'name' ? stored : 'default';
    } catch {
      return 'default';
    }
  });
  const [search, setSearch] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem('bops.cred.view', view);
    } catch {
      /* storage unavailable */
    }
  }, [view]);
  useEffect(() => {
    try {
      localStorage.setItem('bops.cred.sort', sort);
    } catch {
      /* storage unavailable */
    }
  }, [sort]);

  const toggleInfo = useCallback((id: string) => setOpenInfoId((prev) => (prev === id ? null : id)), []);
  const closeModal = useCallback(() => setModalId(null), []);

  const expiringTypes = useMemo(() => new Set(expiringCredentials.map((e) => e.connector_type)), [expiringCredentials]);

  const handleModalSuccess = useCallback(() => {
    closeModal();
    credentialUpdated();
  }, [closeModal, credentialUpdated]);

  // TODO: restore when bopsApi.postSyncCredential exists
  // const [syncingId, setSyncingId] = useState<string | null>(null);
  // const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // useEffect(() => () => { if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current); }, []);
  // const handleSync = useCallback(
  //   (id: string) => {
  //     if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
  //     setSyncingId(id);
  //     credentialUpdated();
  //     syncTimeoutRef.current = setTimeout(() => setSyncingId((current) => (current === id ? null : current)), 1200);
  //   },
  //   [credentialUpdated],
  // );

  const { active, available } = useMemo(() => {
    const all: CredentialEntry[] = [];

    credentials.forEach((item) => {
      const id = item.credential.createdAt
        ? `active-${item.credential.connectorType}-${item.credential.id ?? 'new'}`
        : `available-${item.credential.connectorType}`;
      const card = buildCardProps(item, expiringTypes, () => setModalId(id), t);
      all.push({ card, raw: item, id });
    });

    const activeCards = all.filter(({ card }) => card.status !== 'available');
    const availableCards = all.filter(
      ({ card, raw }) => card.status === 'available' && AVAILABLE_CONNECTOR_ALLOWLIST.has(raw.name),
    );

    return { active: activeCards, available: availableCards };
  }, [credentials, expiringTypes, t]);

  const allEntries = useMemo(() => {
    const base = [...active, ...(config.isGs1Flavor ? available : [])];

    const filtered = search
      ? base.filter(
          ({ card }) =>
            card.name.toLowerCase().includes(search.toLowerCase()) ||
            card.platform.toLowerCase().includes(search.toLowerCase()),
        )
      : base;

    if (sort === 'status') {
      return [...filtered].sort(
        (a, b) =>
          (STATUS_SORT_RANK[a.card.status] ?? 99) - (STATUS_SORT_RANK[b.card.status] ?? 99) ||
          a.card.name.localeCompare(b.card.name),
      );
    }
    if (sort === 'name') {
      return [...filtered].sort((a, b) => a.card.name.localeCompare(b.card.name));
    }
    return filtered;
  }, [active, available, search, sort]);

  const modalEntry = useMemo(() => allEntries.find((e) => e.id === modalId) ?? null, [allEntries, modalId]);

  const renderRowActions = useCallback(
    ({ card, id }: CredentialEntry): React.ReactNode => {
      if (card.readOnly) return null;
      if (card.status === 'available') {
        return (
          <Button size="sm" className="h-8 bg-brand-600 text-white hover:bg-brand-700" onClick={() => setModalId(id)}>
            {t('Credentials.Card.Connect')}
          </Button>
        );
      }
      return (
        <Button
          variant="outline"
          size="sm"
          className="h-8 border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          onClick={() => setModalId(id)}>
          <Settings2 className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
          {t('Credentials.Card.Edit')}
        </Button>
      );
    },
    [t],
  );

  const isEmpty = allEntries.length === 0 && !search;

  return (
    <div className="h-full w-full">
      <PageWrapper title={t('Credentials.Title')}>
        {isEmpty && <EmptyState />}

        {(allEntries.length > 0 || search) && (
          <>
            {/* Toolbar */}
            <div className="mb-4 flex items-center justify-between gap-3">
              <span className="text-sm text-gray-500">{t('Credentials.CountLabel', { count: allEntries.length })}</span>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search
                    className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400 pointer-events-none"
                    strokeWidth={2}
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('Credentials.Search')}
                    className="h-9 w-48 rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-200 transition-colors"
                  />
                </div>
                <div className="relative">
                  <select
                    value={sort}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'default' || v === 'status' || v === 'name') setSort(v);
                    }}
                    className="h-9 rounded-lg border border-gray-200 bg-white pl-3 pr-8 text-sm text-gray-700 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-200 transition-colors appearance-none cursor-pointer">
                    <option value="default">{t('Credentials.SortDefault')}</option>
                    <option value="status">{t('Credentials.SortByStatus')}</option>
                    <option value="name">{t('Credentials.SortByName')}</option>
                  </select>
                  <ChevronDown
                    className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                    strokeWidth={2}
                  />
                </div>
                <div className="flex gap-0.5 rounded-lg border border-gray-200 bg-gray-100 p-0.5">
                  {(
                    [
                      ['cards', LayoutGrid],
                      ['list', List],
                    ] as const
                  ).map(([key, Icon]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setView(key)}
                      aria-label={t(key === 'cards' ? 'Credentials.ViewCards' : 'Credentials.ViewList')}
                      className={cn(
                        'inline-flex h-7 w-7 items-center justify-center rounded-md transition-all',
                        view === key ? 'bg-white text-brand-600 shadow-sm' : 'text-gray-400 hover:text-gray-600',
                      )}>
                      <Icon className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {allEntries.length === 0 && search && (
              <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-12 text-center">
                <span className="text-sm text-gray-500">{t('Credentials.EmptySearch', { term: search })}</span>
              </div>
            )}

            {view === 'cards' && allEntries.length > 0 && (
              <div className="flex flex-col gap-8">
                {sort === 'default' && !search ? (
                  <>
                    {active.length > 0 && (
                      <section>
                        <SectionLabel label={t('Credentials.SectionActive')} count={active.length} />
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                          {active.map((entry) => (
                            <CredentialCard
                              key={entry.id}
                              entry={entry}
                              isInfoOpen={openInfoId === entry.id}
                              toggleInfo={toggleInfo}
                            />
                          ))}
                        </div>
                      </section>
                    )}
                    {config.isGs1Flavor && available.length > 0 && (
                      <section>
                        <SectionLabel label={t('Credentials.SectionAvailable')} count={available.length} />
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                          {available.map((entry) => (
                            <CredentialCard
                              key={entry.id}
                              entry={entry}
                              isInfoOpen={openInfoId === entry.id}
                              toggleInfo={toggleInfo}
                            />
                          ))}
                        </div>
                      </section>
                    )}
                  </>
                ) : (
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                    {allEntries.map((entry) => (
                      <CredentialCard
                        key={entry.id}
                        entry={entry}
                        isInfoOpen={openInfoId === entry.id}
                        toggleInfo={toggleInfo}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {view === 'list' && allEntries.length > 0 && (
              <CredentialListView entries={allEntries} renderRowActions={renderRowActions} />
            )}
          </>
        )}
      </PageWrapper>

      {modalEntry && <CredentialModal entry={modalEntry} onClose={closeModal} onSuccess={handleModalSuccess} />}
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { credentials } = state;
  const expiringCredentialsList = credentials?.expires_credentials || [];
  return { expiringCredentials: expiringCredentialsList };
};

export default connect(mapStateToProps)(Credentials);
